import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { MapPin, Clock, Phone, Navigation, ArrowRight, Car } from 'lucide-react';
import { useQueueSSE } from '@/lib/useSSE';

interface ShopDetail {
  id: string;
  name: string;
  nameAr: string;
  slug: string;
  address: string;
  addressAr: string;
  phone: string;
  whatsapp: string;
  bayCount: number;
  estimatedWashMinutes: number;
  actualAvgWashMinutes: number | null;
  openTime: string;
  closeTime: string;
  lat: string | null;
  lng: string | null;
  sedanPrice: number;
  suvPrice: number;
  largeSuvPrice: number;
  busyHours?: Array<{ hour: number; count: number }>;
}

interface ShopDetailProps {
  params: { slug: string };
}

function formatHour(hour: number): string {
  if (hour === 0) return '12 ص';
  if (hour < 12) return `${hour} ص`;
  if (hour === 12) return '12 م';
  return `${hour - 12} م`;
}

export default function ShopDetailPage({ params }: ShopDetailProps) {
  const { slug } = params;
  const [shop, setShop] = useState<ShopDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queueData = useQueueSSE(slug);

  useEffect(() => {
    fetch(`/api/shops/${slug}`)
      .then(res => res.json())
      .then(data => {
        setShop(data.shop || data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [slug]);

  if (isLoading) {
    return (
      <div className="py-8 px-4 max-w-3xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-5 bg-gray-100 rounded w-2/3" />
          <div className="h-32 bg-gray-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="py-16 px-4 text-center">
        <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 mb-4">المغسلة غير موجودة</p>
        <Link href="/shops" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          العودة للمغاسل
        </Link>
      </div>
    );
  }

  const avgWash = shop.actualAvgWashMinutes || shop.estimatedWashMinutes;
  const queueCount = queueData?.count || 0;
  const waitMinutes = queueData?.estimatedWaitMinutes || 0;
  const maxBusy = shop.busyHours ? Math.max(...shop.busyHours.map(h => h.count), 1) : 1;

  const isOpen = (): boolean => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [openH, openM] = shop.openTime.split(':').map(Number);
    const [closeH, closeM] = shop.closeTime.split(':').map(Number);
    return currentMinutes >= openH * 60 + openM && currentMinutes < closeH * 60 + closeM;
  };

  const open = isOpen();

  return (
    <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Back link */}
        <Link href="/shops" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium">
          <ArrowRight className="w-4 h-4" />
          جميع المغاسل
        </Link>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{shop.nameAr || shop.name}</h1>
          <div className="flex items-center gap-1.5 mt-1.5 text-gray-500">
            <MapPin className="w-4 h-4" />
            <span>{shop.addressAr || shop.address}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg ${
              open ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <div className={`w-2 h-2 rounded-full ${open ? 'bg-green-500' : 'bg-gray-400'}`} />
              {open ? `مفتوح | يغلق ${shop.closeTime}` : `مغلق | يفتح ${shop.openTime}`}
            </span>
          </div>
        </div>

        {/* Live Queue Status */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h2 className="text-base font-bold text-gray-900 mb-4">الحالة الآن</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className={`text-2xl font-bold ${queueCount === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                {queueCount === 0 ? 'فوري' : `~${waitMinutes} د`}
              </p>
              <p className="text-xs text-gray-500 mt-1">وقت الانتظار</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{shop.bayCount}</p>
              <p className="text-xs text-gray-500 mt-1">مكان غسيل</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{queueCount}</p>
              <p className="text-xs text-gray-500 mt-1">في الطابور</p>
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h2 className="text-base font-bold text-gray-900 mb-3">أداء المغسلة</h2>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary-600" />
            <span className="text-sm text-gray-700">
              متوسط وقت الغسيل: <span className="font-bold">{avgWash} دقيقة</span>
            </span>
          </div>
        </div>

        {/* Busy Hours */}
        {shop.busyHours && shop.busyHours.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-base font-bold text-gray-900 mb-4">أفضل أوقات الزيارة</h2>
            <div className="space-y-2">
              {shop.busyHours
                .filter(h => h.hour >= 8 && h.hour <= 22)
                .map((h) => {
                  const ratio = h.count / maxBusy;
                  const barColor = ratio > 0.75
                    ? 'bg-red-400'
                    : ratio > 0.5
                    ? 'bg-orange-400'
                    : ratio > 0.25
                    ? 'bg-yellow-400'
                    : 'bg-green-400';
                  const label = ratio > 0.75
                    ? 'مزدحم جداً'
                    : ratio > 0.5
                    ? 'مزدحم'
                    : ratio > 0.25
                    ? 'معتدل'
                    : 'هادئ';

                  return (
                    <div key={h.hour} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-10 text-left" dir="ltr">
                        {formatHour(h.hour)}
                      </span>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${barColor} rounded-full transition-all`}
                          style={{ width: `${(h.count / maxBusy) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 w-16 text-left">{label}</span>
                    </div>
                  );
                })}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              افضل وقت: الساعات ذات اللون الأخضر
            </p>
          </div>
        )}

        {/* Contact Actions */}
        <div className="grid grid-cols-2 gap-3">
          {shop.phone && (
            <a
              href={`tel:${shop.phone}`}
              className="flex items-center justify-center gap-2 bg-white rounded-xl py-3 shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <Phone className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-gray-700">اتصل</span>
            </a>
          )}
          {shop.lat && shop.lng && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-white rounded-xl py-3 shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <Navigation className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-gray-700">الاتجاهات</span>
            </a>
          )}
        </div>

        {/* Subscribe CTA */}
        <div className="bg-primary-50 rounded-2xl p-5 text-center border border-primary-100">
          <h3 className="font-bold text-primary-900 mb-1">اشترك ووفر</h3>
          <p className="text-sm text-primary-700 mb-4">اشتراك شهري واحد لغسيل سيارتك في أي مغسلة شريكة</p>
          <Link
            href="/"
            className="inline-block bg-primary-600 text-white font-medium rounded-xl px-6 py-2.5 hover:bg-primary-700 transition-colors"
          >
            اشترك الآن
          </Link>
        </div>
      </div>
    </div>
  );
}
